// src/components/Dashboard/RoleDashboard/RoleExtras/RoleReport.jsx

import React, { useState, useEffect, useMemo } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./RoleReport.css";
import { resolveId, reportListUrl, reportSubmitUrl } from "../roleApi";

const VALID_ROLES = ["agent", "scout", "club"];

function normalizeRole(r) {
    const role = String(r || "").toLowerCase();
    return VALID_ROLES.includes(role) ? role : "agent";
}

function normalizeFrontendLink(input) {
    const raw = String(input || "").trim();
    if (!raw) return "";

    const origin =
        typeof window !== "undefined" ? window.location.origin : "";

    // If user pasted a path like "/agent-dashboard/..."
    if (raw.startsWith("/")) return `${origin}${raw}`;

    // If user pasted "agent-dashboard/..." (no leading slash)
    if (!/^https?:\/\//i.test(raw) && !raw.startsWith("www.")) {
        return `${origin}/${raw.replace(/^\/+/, "")}`;
    }

    // If user pasted "www.example.com/..."
    if (raw.startsWith("www.")) return `https://${raw}`;

    // Already absolute http(s)
    return raw;
}

/* ------------------------------------------------------------------ */
/* Role-aware update options (can be extended later per role)          */
/* ------------------------------------------------------------------ */

const UPDATE_OPTIONS = {
    agent: [
        "Player Signed for a Club",
        "Player Received Trials Offer",
        "Player Signed Official Contract with the Agency",
        "Player Signed a Sponsorship Deal",
        "Player Transferred to a New Club",
        "Other",
    ],
    scout: [
        "Player Recommended to Club",
        "Match Report Submitted",
        "Player Shortlisted",
        "Trial Recommendation",
        "Other",
    ],
    club: [
        "Player Signed",
        "Trial Offered",
        "Player Shortlisted",
        "Contract Negotiation Started",
        "Other",
    ],
};

const RoleReport = ({
    role: roleProp,
    userId,
    id,
    agentId,
    scoutId,
    clubId,
}) => {
    /* -------------------- Role + Actor -------------------- */

    const role = normalizeRole(
        roleProp ||
        (typeof window !== "undefined" &&
            (localStorage.getItem("role") || localStorage.getItem("userRole"))) ||
        "agent"
    );

    const actorId = resolveId({
        role,
        id,
        userId,
        agentId,
        scoutId,
        clubId,
    });

    /* -------------------- State -------------------- */

    const [activeTab, setActiveTab] = useState("report");
    const [submittedReports, setSubmittedReports] = useState([]);

    const [form, setForm] = useState({
        updateType: "",
        generalExplanation: "",
        playerProfileLink: "",
        followUps: {},
        files: null,
    });

    /* -------------------- Derived -------------------- */

    const updateOptions = useMemo(
        () => UPDATE_OPTIONS[role] || UPDATE_OPTIONS.agent,
        [role]
    );

    const pageTitle =
        role === "agent"
            ? "Agent Reporting"
            : role === "scout"
                ? "Scout Reporting"
                : "Club Reporting";

    const pageSubtitle =
        "Submit updates for players you discovered on VSN and connected to real-world opportunities.";

    /* -------------------- Fetch Submitted Reports -------------------- */

    useEffect(() => {
        if (activeTab !== "submitted" || !actorId) return;

        const path = reportListUrl(role, actorId);

        apiFetch(path)
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(`Failed to fetch reports (${res.status})`);
                }
                return res.json();
            })
            .then((data) => setSubmittedReports(data || []))
            .catch((err) =>
                console.error("[RoleReport] Error fetching reports:", err)
            );
    }, [activeTab, actorId, role]);

    /* -------------------- Handlers -------------------- */

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleFollowUpChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            followUps: { ...prev.followUps, [name]: value },
        }));
    };

    const handleFileChange = (e) => {
        setForm((prev) => ({ ...prev, files: e.target.files }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!actorId) {
            alert("User ID missing. Cannot submit report.");
            return;
        }

        const formData = new FormData();
        formData.append("role", role);
        formData.append("actorId", actorId);
        formData.append("updateType", form.updateType);
        formData.append("generalExplanation", form.generalExplanation);
        formData.append(
            "playerProfileLink",
            normalizeFrontendLink(form.playerProfileLink)
        );

        formData.append("followUps", JSON.stringify(form.followUps));

        if (form.files) {
            for (let i = 0; i < form.files.length; i++) {
                formData.append("files", form.files[i]);
            }
        }

        const path = reportSubmitUrl(role);

        try {
            const res = await apiFetch(path, {
                method: "POST",
                body: formData,
            });

            let data = null;
            try {
                data = await res.json();
            } catch {
                data = null;
            }

            if (res.ok) {
                alert("Report submitted successfully!");
                setForm({
                    updateType: "",
                    generalExplanation: "",
                    playerProfileLink: "",
                    followUps: {},
                    files: null,
                });
            } else {
                alert(data?.message || `Submission failed (${res.status})`);
            }
        } catch (err) {
            console.error("[RoleReport] Submission error:", err);
            alert("Server error. Please try again later.");
        }
    };

    /* -------------------- Follow-up Questions -------------------- */

    const renderFollowUpQuestions = () => {
        const q = [];

        switch (form.updateType) {
            case "Player Signed for a Club":
                q.push(
                    { label: "Club Name", name: "clubName" },
                    { label: "City, Country", name: "location" },
                    { label: "Contract Duration", name: "contractDuration" },
                    { label: "Transfer Fee (if any)", name: "transferFee" }
                );
                break;

            case "Player Received Trials Offer":
                q.push(
                    { label: "Club Name", name: "trialClubName" },
                    { label: "Trial Dates", name: "trialDates" }
                );
                break;

            case "Other":
                q.push(
                    { label: "Description", name: "description" },
                    { label: "Outcome", name: "outcome" }
                );
                break;

            default:
                return null;
        }

        return q.map(({ label, name }) => (
            <div key={name} className="role-report-form-group">
                <label>{label}:</label>
                <input
                    type="text"
                    name={name}
                    value={form.followUps[name] || ""}
                    onChange={handleFollowUpChange}
                />
            </div>
        ));
    };

    const formatLabel = (key) =>
        key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

    /* -------------------- UI -------------------- */

    return (
        <div className={`role-report-wrapper ${role}-report`}>
            <div className="top-header">
                <h1 className="role-page-title">{pageTitle}</h1>
                <p className="role-page-subtitle">{pageSubtitle}</p>

                <div className="toggle-buttons">
                    <button
                        className={activeTab === "report" ? "active-tab" : ""}
                        onClick={() => setActiveTab("report")}
                    >
                        {pageTitle}
                    </button>
                    <button
                        className={activeTab === "submitted" ? "active-tab" : ""}
                        onClick={() => setActiveTab("submitted")}
                    >
                        Submitted Reports
                    </button>
                </div>
            </div>

            {activeTab === "report" && (
                <div className="role-report-container">
                    <form onSubmit={handleSubmit} className="role-report-form">
                        <div className="role-report-form-group">
                            <label>Type of Update:</label>
                            <select
                                name="updateType"
                                value={form.updateType}
                                onChange={handleChange}
                                required
                            >
                                <option value="">-- Select Update Type --</option>
                                {updateOptions.map((opt, idx) => (
                                    <option key={idx} value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="role-report-form-group">
                            <label>General Explanation:</label>
                            <textarea
                                name="generalExplanation"
                                value={form.generalExplanation}
                                onChange={handleChange}
                                rows="4"
                                required
                            />
                        </div>

                        <div className="role-report-form-group">
                            <label>VSN Player Profile Link:</label>
                            <input
                                type="text"
                                name="playerProfileLink"
                                value={form.playerProfileLink}
                                onChange={handleChange}
                                placeholder="Paste VSN player profile link (e.g. https://www.virtualscoutingnetwork.com/player-profile/ghaucho)"
                                required
                            />
                        </div>

                        {form.updateType && (
                            <div className="role-report-follow-up-section">
                                <h4>Follow-up Questions</h4>
                                {renderFollowUpQuestions()}
                            </div>
                        )}

                        <div className="role-report-form-group">
                            <label>Upload Supporting Documents:</label>
                            <input type="file" multiple onChange={handleFileChange} />
                        </div>

                        <button type="submit" className="role-report-submit-button">
                            Submit Report
                        </button>
                    </form>
                </div>
            )}

            {activeTab === "submitted" && (
                <div className="submitted-reports-cards">
                    {submittedReports.length === 0 ? (
                        <p>No reports submitted yet.</p>
                    ) : (
                            submittedReports.map((report, idx) => (
                                <div key={idx} className="submitted-report-card">
                                    <h3>{report.updateType}</h3>

                                    <p>
                                        <strong>Explanation:</strong>{" "}
                                        {report.generalExplanation}
                                    </p>

                                    {report.playerProfileLink && (
                                        <p>
                                            <strong>Player Profile:</strong>{" "}
                                            <a
                                                href={report.playerProfileLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                View Player Profile
                                            </a>
                                        </p>
                                    )}

                                    {report.followUps &&
                                        Object.keys(report.followUps).length > 0 && (
                                            <ul>
                                                {Object.entries(report.followUps).map(([k, v]) => (
                                                    <li key={k}>
                                                        <strong>{formatLabel(k)}:</strong> {v}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                    {report.createdAt && (
                                        <p className="report-submitted-time">
                                            <strong>Submitted:</strong>{" "}
                                            {new Date(report.createdAt).toLocaleString()}
                                        </p>
                                    )}
                                </div>
                            ))
                    )}
                </div>
            )}
        </div>
    );
};

export default RoleReport;
